"use client"

import React, { useState, useRef, useMemo, useCallback } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Search, X, ArrowLeft, ArrowUp, ArrowDown } from 'lucide-react';
import { CustomFilterIcon } from '@/components/custom-filter-icon';
import { ModernDatePicker } from '@/components/modern-date-picker';
import { useWorkflowApi } from '@/hooks/use-workflow-api';
import { useWorkflow } from '@/context/workflow-context-simple';
import { useWorkflowRowNavigation } from '@/hooks/use-workflow-row-navigation';
import { useTranslation } from '@/hooks/use-translation';
import { useLanguage } from '@/context/language-context';
import { Z_INDEX } from '@/lib/z-index';
import type { WorkflowItem } from '@/types/workflow';
import { isSearchableField, isDateField, isNumberField, getSearchColumnPath, buildFilterForField } from '@/utils/column-type-helpers';
import { formatDateOnly } from '@/utils/format-helpers';

interface WorkerSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onNavigate?: () => void;
  /**
   * Fired when the user clicks a pre-onboarding result row (ATS / VMS / CR).
   * Those rows live in isolated per-tab providers the modal can't drive with
   * setCurrentStage/setStageFilters, so the hub owns the redirect: switch to
   * the family's tab and hand the row filter off for the tab's grid to adopt.
   */
  onNavigateToRequestTab?: (documentType: string, item: WorkflowItem) => void;
}

// Display labels for the Stage pill on pre-onboarding rows — all three families
// are "Requests"-stage, so the pill shows the family instead.
const REQUEST_FAMILY_LABELS: Record<string, string> = {
  AtsPlacementRequest: 'ATS',
  VmsRequest: 'VMS',
  ClientRequest: 'Client Request',
};

// Status codes are stored camel-cased ("RequestAccepted", "OnHold") — split
// them for display so the Status pill reads as words. Applied at row-mapping
// time so the table's text filter and sort operate on the displayed form.
const humanizeStatusCode = (value: unknown): string =>
  String(value ?? '').replace(/([a-z0-9])([A-Z])/g, '$1 $2');

type ViewMode = 'search' | 'results';

type SortDirection = 'asc' | 'desc' | null;
type SortConfig = {
  column: string;
  direction: SortDirection;
};

export function WorkerSearchModal({ isOpen, onClose, onNavigate, onNavigateToRequestTab }: WorkerSearchModalProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('search');
  const [searchValue, setSearchValue] = useState('');
  const [suggestions, setSuggestions] = useState<string[]>([]);
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [selectedWorker, setSelectedWorker] = useState<string | null>(null);
  const [searchResults, setSearchResults] = useState<WorkflowItem[]>([]);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [resultsPage, setResultsPage] = useState(1);
  
  // Sorting and filtering state for results table
  const [sortConfig, setSortConfig] = useState<SortConfig>({ column: '', direction: null });
  const [columnFilters, setColumnFilters] = useState<Record<string, string | { start: string | null; end: string | null }>>({
    WorkerFullName: '',
    ClientName: '',
    FboWorkOrderNumber: '',
    StartDate: { start: null, end: null },
    EndDate: { start: null, end: null },
    Stage: '',
    Status: ''
  });
  const [openFilterColumn, setOpenFilterColumn] = useState<string | null>(null);
  

  const suggestionsTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  
  // Animation and timing constants
  const DEBOUNCE_DELAY = 500;
  const ANIMATION_DURATION = 300;
  const ANIMATION_BUFFER = 50;
  const ITEMS_PER_PAGE = 10;

  const { fetchSearchSuggestions, searchWorkflowItems } = useWorkflowApi();
  const { configuration, stages, state } = useWorkflow();
  const { navigateToRow } = useWorkflowRowNavigation();
  const { t } = useTranslation();
  const { language } = useLanguage();

  // Get field configurations for the grid columns
  const getFieldConfig = useCallback((columnCode: string) => {
    if (!configuration?.fieldsInUse) return null;
    return configuration.fieldsInUse.find(
      (f: any) => f.columnCode === columnCode
    );
  }, [configuration]);

  // Get localized column name
  const getColumnName = useCallback((columnCode: string) => {
    // The results now mix WO rows with pre-onboarding rows, so this column
    // carries WO numbers, ATS placement ids and VMS external request ids —
    // the WO-specific config label ("FBO Work Order Number") would mislead.
    if (columnCode === 'FboWorkOrderNumber') {
      return language === 'fr' ? 'Numéro de référence' : 'Reference #';
    }
    // Synthetic column: WO rows alias WorkOrderStatusCode, request rows their
    // own Status — no single field config to pull a label from.
    if (columnCode === 'Status') {
      return t('status') || 'Status';
    }
    const field = getFieldConfig(columnCode);
    if (!field) return columnCode;
    return language === 'fr' ? field.columnNameFrench : field.columnNameEnglish;
  }, [getFieldConfig, language, t]);

  // Handle column sorting
  const handleSort = useCallback((column: string) => {
    setSortConfig(prevConfig => {
      if (prevConfig.column === column) {
        // Cycle through: asc -> desc -> null
        const newDirection: SortDirection = 
          prevConfig.direction === 'asc' ? 'desc' :
          prevConfig.direction === 'desc' ? null : 'asc';
        return { column: newDirection ? column : '', direction: newDirection };
      } else {
        return { column, direction: 'asc' };
      }
    });
    setResultsPage(1); // Reset to first page when sorting
  }, []);

  // Handle column filter change
  const handleColumnFilterChange = useCallback((column: string, value: string | { start: string | null; end: string | null }) => {
    setColumnFilters(prev => ({ ...prev, [column]: value }));
    setResultsPage(1); // Reset to first page when filtering
  }, []);

  // Clear all filters
  const clearAllFilters = useCallback(() => {
    setColumnFilters({
      WorkerFullName: '',
      ClientName: '',
      FboWorkOrderNumber: '',
      StartDate: { start: null, end: null },
      EndDate: { start: null, end: null },
      Stage: '',
      Status: ''
    });
    setSortConfig({ column: '', direction: null });
    setResultsPage(1);
    setOpenFilterColumn(null);
  }, []);

  // Toggle filter popover
  const toggleFilterPopover = useCallback((column: string) => {
    setOpenFilterColumn(prev => prev === column ? null : column);
  }, []);

  // Check if column has active filter
  const hasActiveFilter = useCallback((column: string) => {
    const filter = columnFilters[column];
    if (typeof filter === 'string') {
      return filter.trim() !== '';
    } else if (filter && typeof filter === 'object' && 'start' in filter) {
      return filter.start !== null || filter.end !== null;
    }
    return false;
  }, [columnFilters]);

  // Fetch suggestions with debounce
  const fetchSuggestions = useCallback(async (searchText: string) => {
    if (!searchText || searchText.trim().length < 2) {
      setSuggestions([]);
      return;
    }

    setIsLoadingSuggestions(true);
    try {
      // 'All' = the backend queries the WorkOrder AND unified request indexes in
      // parallel and merges, so workers with no Assignment yet (pre-onboarding
      // only) still autocomplete. The old DocumentType filter was never applied
      // server-side (the suggestions path ignores the Filter body field).
      const response = await fetchSearchSuggestions(searchText.trim(), 'WorkerFullName', 15, undefined, 'All');
      let suggestionsArray: string[] = [];
      
      if (Array.isArray(response)) {
        suggestionsArray = response;
      } else if (response && response.value && Array.isArray(response.value)) {
        suggestionsArray = response.value;
      }
      
      setSuggestions(suggestionsArray);
    } catch (error) {
      console.error('Failed to fetch suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoadingSuggestions(false);
    }
  }, [fetchSearchSuggestions]);

  // Debounced search handler
  const handleSearchChange = useCallback((value: string) => {
    setSearchValue(value);

    if (suggestionsTimeoutRef.current) {
      clearTimeout(suggestionsTimeoutRef.current);
    }

    // Show loading immediately while debounce waits (avoids flash of "No workers found")
    if (value.trim().length >= 2) {
      setIsLoadingSuggestions(true);
    } else {
      setIsLoadingSuggestions(false);
      setSuggestions([]);
    }

    suggestionsTimeoutRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, DEBOUNCE_DELAY);
  }, [fetchSuggestions]);

  // Handle suggestion click - search for worker and show results
  const handleSuggestionClick = useCallback(async (workerName: string) => {
    setSelectedWorker(workerName);
    setSearchValue(workerName);
    setIsLoadingResults(true);
    setIsTransitioning(true);
    setResultsPage(1);
    setSuggestions([]);

    const timeoutPromise = new Promise<never>((_, reject) => 
      setTimeout(() => reject(new Error('Search request timeout')), 15000)
    );

    try {
      const escapedWorker = workerName.replace(/'/g, "''");
      const woFilter = `DocumentType eq 'Assignment' and search.ismatch('${escapedWorker}', 'WorkerFullName', 'full', 'all') and Stage ne '' and Stage ne null and Stage ne 'Discarded'`;
      const woSelectFields = 'WorkerFullName,ClientName,FboWorkOrderNumber,StartDate,EndDate,Stage,WorkOrderStatusCode,id,FboId,ObId';

      // Pre-onboarding sweep: one combined call over the unified request index
      // ('Requests' pseudo entity type = ATS + VMS + ClientRequest, scoped
      // server-side). Terminal rows are included on purpose — Status is shown
      // so users get the worker's request history, mirroring how WO search
      // includes Complete-stage rows.
      const requestFilter = `search.ismatch('${escapedWorker}', 'WorkerFullName', 'full', 'all')`;
      const requestSelectFields = 'WorkerFullName,Client/Name,FboWorkOrderId,AtsPlacementId,VmsExternalRequestId,StartDate,EndDate,DocumentType,Status,RequestId,id';

      const [woResult, requestResult] = await Promise.race([
        Promise.allSettled([
          searchWorkflowItems(
            undefined,
            undefined,
            [],
            1,
            1000,
            '*',
            woSelectFields,
            woFilter,
            'CreatedDateTime desc'
          ),
          searchWorkflowItems(
            undefined,
            undefined,
            [],
            1,
            1000,
            '*',
            requestSelectFields,
            requestFilter,
            'RequestDate desc',
            undefined,
            'Requests'
          ),
        ]),
        timeoutPromise
      ]);

      // Alias each family's status field onto the shared Status column:
      // WO rows carry WorkOrderStatusCode, request rows their own Status.
      const woRows = woResult.status === 'fulfilled'
        ? (woResult.value.data || []).map((row: any) => ({
            ...row,
            status: humanizeStatusCode(row.workOrderStatusCode),
          }))
        : [];
      if (woResult.status === 'rejected') console.error('Worker search (WorkOrder) failed:', woResult.reason);
      if (requestResult.status === 'rejected') console.error('Worker search (Requests) failed:', requestResult.reason);

      // Alias the request-row fields onto the column names the table reads
      // (getCellValue falls back to camelCase keys), mark the row so click
      // routing and the Stage cell can branch on it.
      const requestRows = requestResult.status === 'fulfilled'
        ? (requestResult.value.data || []).map((row: any) => ({
            ...row,
            clientName: row.client?.name ?? row.client?.Name ?? '',
            // The Reference # column carries the family's own identifier: ATS
            // placement id / VMS external request id, falling back to the linked
            // FBO work order id once one exists.
            fboWorkOrderNumber: row.documentType === 'AtsPlacementRequest'
              ? (row.atsPlacementId != null ? String(row.atsPlacementId) : '')
              : (row.vmsExternalRequestId
                || (row.fboWorkOrderId != null ? String(row.fboWorkOrderId) : '')),
            stage: REQUEST_FAMILY_LABELS[row.documentType] || 'Request',
            status: humanizeStatusCode(row.status),
            isRequestRow: true,
          }))
        : [];

      setSearchResults([...woRows, ...requestRows]);
      setViewMode('results');
      
      setTimeout(() => {
        setIsTransitioning(false);
        setIsLoadingResults(false);
      }, ANIMATION_DURATION + ANIMATION_BUFFER);
    } catch (error) {
      console.error('Failed to fetch search results:', error);
      setSearchResults([]);
      setViewMode('results');
      setIsTransitioning(false);
      setIsLoadingResults(false);
    }
  }, [searchWorkflowItems]);

  // Helper to get cell value
  const getCellValue = (item: WorkflowItem, columnCode: string): any => {
    const camelCaseKey = columnCode.charAt(0).toLowerCase() + columnCode.slice(1);
    return (item as any)[columnCode] ?? (item as any)[camelCaseKey] ?? undefined;
  };

  // Apply filtering and sorting to search results
  const memoizedResults = useMemo(() => {
    let filteredResults = [...searchResults];

    // Apply column filters
    const hasFilters = Object.entries(columnFilters).some(([column, filter]) => {
      // Check for non-empty filters
      if (typeof filter === 'string') {
        return filter.trim() !== '';
      } else if (filter && typeof filter === 'object' && 'start' in filter) {
        return filter.start !== null || filter.end !== null;
      }
      return false;
    });
    
    if (hasFilters) {
      filteredResults = filteredResults.filter(item => {
        return Object.entries(columnFilters).every(([column, filterValue]) => {
          // Skip empty filters
          if (typeof filterValue === 'string') {
            if (!filterValue.trim()) return true;
          } else if (filterValue && typeof filterValue === 'object' && 'start' in filterValue) {
            if (!filterValue.start && !filterValue.end) return true;
          } else {
            return true;
          }
          
          let cellValue = getCellValue(item, column);
          
          // Handle date range filtering
          if (filterValue && typeof filterValue === 'object' && 'start' in filterValue) {
            if (!cellValue) return false;
            
            const itemDate = new Date(cellValue);
            if (isNaN(itemDate.getTime())) return false;
            
            // Check start date
            if (filterValue.start) {
              const startDate = new Date(filterValue.start + 'T00:00:00Z');
              if (itemDate < startDate) return false;
            }
            
            // Check end date
            if (filterValue.end) {
              const endDate = new Date(filterValue.end + 'T23:59:59Z');
              if (itemDate > endDate) return false;
            }
            
            return true;
          } else {
            // Handle text filtering
            // Handle dates for display formatting
            const fieldConfig = configuration?.fieldsInUse?.find(f => f.columnCode === column);
            if (fieldConfig && isDateField(fieldConfig)) {
              cellValue = formatDateOnly(cellValue, language);
            }
            
            const cellString = String(cellValue || '').toLowerCase();
            const filterString = String(filterValue || '').toLowerCase();
            
            return cellString.includes(filterString);
          }
        });
      });
    }

    // Apply sorting
    if (sortConfig.column && sortConfig.direction) {
      filteredResults.sort((a, b) => {
        let aValue = getCellValue(a, sortConfig.column);
        let bValue = getCellValue(b, sortConfig.column);

        // Handle dates for sorting  
        const sortFieldConfig = configuration?.fieldsInUse?.find(f => f.columnCode === sortConfig.column);
        if (sortFieldConfig && isDateField(sortFieldConfig)) {
          aValue = aValue ? new Date(aValue) : new Date(0);
          bValue = bValue ? new Date(bValue) : new Date(0);
        }
        
        // Handle null/undefined values
        if (aValue == null && bValue == null) return 0;
        if (aValue == null) return 1;
        if (bValue == null) return -1;

        // Convert to strings for comparison if not dates
        if (!(aValue instanceof Date)) {
          aValue = String(aValue).toLowerCase();
          bValue = String(bValue).toLowerCase();
        }

        let result = 0;
        if (aValue < bValue) result = -1;
        else if (aValue > bValue) result = 1;

        return sortConfig.direction === 'desc' ? -result : result;
      });
    }

    return filteredResults;
  }, [searchResults, columnFilters, sortConfig, getCellValue, language]);

  // Paginate results
  const paginatedResults = useMemo(() => {
    const startIndex = (resultsPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    return memoizedResults.slice(startIndex, endIndex);
  }, [memoizedResults, resultsPage]);

  const totalPages = Math.ceil(memoizedResults.length / ITEMS_PER_PAGE);

  // Handle clear search
  const handleClearSearch = useCallback(() => {
    setSearchValue('');
    setSelectedWorker(null);
    setSearchResults([]);
    setViewMode('search');
    setResultsPage(1);
    setSuggestions([]);
    setIsTransitioning(false);
    setIsLoadingResults(false);
    // Reset sorting and filtering
    setSortConfig({ column: '', direction: null });
    setColumnFilters({
      WorkerFullName: '',
      ClientName: '',
      FboWorkOrderNumber: '',
      StartDate: { start: null, end: null },
      EndDate: { start: null, end: null },
      Stage: ''
    });
    setOpenFilterColumn(null);
  }, []);


  // Helper to find target stage by stage code
  // Row → stage navigation now lives in use-workflow-row-navigation (shared with the messages inbox).

  // Handle row click — reuse the shared grid navigation so the messages inbox lands on a record by
  // the identical rules (see use-workflow-row-navigation).
  const handleRowClick = useCallback(
    (item: WorkflowItem) => {
      navigateToRow(item as unknown as Record<string, any>, { onNavigate, onClose, onNavigateToRequestTab });
    },
    [navigateToRow, onNavigate, onClose, onNavigateToRequestTab]
  );

  // Reset when modal closes
  React.useEffect(() => {
    if (!isOpen) {
      setViewMode('search');
      setSearchValue('');
      setSuggestions([]);
      setSelectedWorker(null);
      setSearchResults([]);
      setResultsPage(1);
      setIsTransitioning(false);
      setIsLoadingResults(false);
      // Reset sorting and filtering
      setSortConfig({ column: '', direction: null });
      setColumnFilters({
        WorkerFullName: '',
        ClientName: '',
        FboWorkOrderNumber: '',
        StartDate: { start: null, end: null },
        EndDate: { start: null, end: null },
        Stage: ''
      });
      setOpenFilterColumn(null);
    }
  }, [isOpen]);

  // Handle ESC key to close modal
  React.useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen, onClose]);

  // Cleanup timeout
  React.useEffect(() => {
    return () => {
      if (suggestionsTimeoutRef.current) {
        clearTimeout(suggestionsTimeoutRef.current);
      }
    };
  }, []);

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-start justify-center pt-16 animate-in fade-in duration-200"
      style={{ zIndex: Z_INDEX.MODAL_CONTENT }}
    >
      {/* Backdrop with blur */}
      <div 
        className="absolute inset-0 bg-black/20 backdrop-blur-sm animate-in fade-in duration-300"
        onClick={onClose}
      />
      
      {/* Floating container - expands based on view mode */}
      <div className={`relative mx-4 transition-all duration-300 animate-in slide-in-from-top-4 ${
        viewMode === 'search' ? 'w-full max-w-lg' : 'w-full max-w-5xl'
      }`}>
        {/* Search bar */}
        <div className="bg-white rounded-2xl shadow-2xl border border-gray-200/50 overflow-hidden">
          <div className="relative flex items-center">
            {viewMode === 'results' && (
              <button
                onClick={handleClearSearch}
                className="flex-shrink-0 p-4 text-gray-400 hover:text-gray-600 transition-colors"
                type="button"
              >
                <ArrowLeft size={20} />
              </button>
            )}
            <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} style={{
              left: viewMode === 'results' ? '4rem' : '1rem'
            }} />
            <Input
              value={searchValue}
              onChange={(e) => {
                const newValue = e.target.value;
                handleSearchChange(newValue);
                if (viewMode === 'results' && newValue !== selectedWorker) {
                  setViewMode('search');
                  setSearchResults([]);
                  setSelectedWorker(null);
                }
              }}
              placeholder={t('searchWorker') || 'Search for workers...'}
              className={`border-0 focus:ring-0 focus:outline-none bg-transparent placeholder:text-gray-400 py-4 text-lg ${
                viewMode === 'results' ? 'pl-20 pr-12' : 'pl-12 pr-12'
              }`}
              autoFocus={viewMode === 'search'}
            />
            {searchValue && (
              <button
                onClick={handleClearSearch}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                type="button"
              >
                <X size={18} />
              </button>
            )}
          </div>
        </div>

        {viewMode === 'search' ? (
          <>
            {/* Suggestions dropdown */}
            {(suggestions.length > 0 || isLoadingSuggestions || (searchValue.trim().length >= 2)) && (
              <div className="mt-2 bg-white rounded-xl shadow-xl border border-gray-200/50 overflow-hidden max-h-80 animate-in slide-in-from-top-2 fade-in duration-200">
                {isLoadingSuggestions ? (
                  <div className="flex items-center justify-center py-6">
                    <div className="flex items-center gap-3">
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-blue-500 border-t-transparent"></div>
                      <span className="text-gray-600 text-sm">{t('loading') || 'Searching'}...</span>
                    </div>
                  </div>
                ) : suggestions.length > 0 ? (
                  <div className="max-h-72 overflow-y-auto">
                    {suggestions.map((suggestion, index) => (
                      <button
                        key={index}
                        onClick={() => handleSuggestionClick(suggestion)}
                        className="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0 flex items-center gap-3"
                      >
                        <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                          <span className="text-blue-600 font-medium text-sm">
                            {suggestion.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                          </span>
                        </div>
                        <span className="text-gray-900 font-medium">{suggestion}</span>
                      </button>
                    ))}
                  </div>
                ) : isLoadingResults ? (
                  <div className="py-8 text-center">
                    <div className="flex items-center justify-center gap-3">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
                      <span className="text-gray-700">{t('loading') || 'Loading'}...</span>
                    </div>
                  </div>
                ) : searchValue.trim().length >= 2 ? (
                  <div className="py-8 text-center text-gray-500">
                    <div className="w-12 h-12 mx-auto mb-3 bg-gray-100 rounded-full flex items-center justify-center">
                      <Search size={20} className="text-gray-400" />
                    </div>
                    <p className="text-sm">{t('noSuggestions') || 'No workers found'}</p>
                    <p className="text-xs text-gray-400 mt-1">Try a different search term</p>
                  </div>
                ) : null}
              </div>
            )}

            {/* Helper text */}
            {!searchValue && (
              <div className="mt-4 text-center animate-in fade-in duration-300">
                <p className="text-sm text-gray-500">
                {t('searchHelperText') || 'Type at least 2 characters to search for workers'}
                </p>
              </div>
            )}
          </>
        ) : (
          /* Results view */
          <div className="mt-4 bg-white rounded-2xl shadow-xl border border-gray-200/50 overflow-hidden animate-in slide-in-from-top-2 fade-in duration-300">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  {language === 'fr' 
                    ? `Résultats pour "${selectedWorker}"`
                    : `Results for "${selectedWorker}"`
                  }
                </h3>
                {/* Clear filters button - only show if there are active filters or sorting */}
                {(Object.entries(columnFilters).some(([column, filter]) => {
                  if (typeof filter === 'string') {
                    return filter.trim() !== '';
                  } else if (filter && typeof filter === 'object' && 'start' in filter) {
                    return filter.start !== null || filter.end !== null;
                  }
                  return false;
                }) || sortConfig.direction) && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={clearAllFilters}
                    className="text-xs h-8"
                  >
                    Clear All Filters
                  </Button>
                )}
              </div>
              
              {isLoadingResults || isTransitioning ? (
                <div className="flex items-center justify-center py-12">
                  <div className="flex items-center gap-3">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-blue-500 border-t-transparent"></div>
                    <span className="text-gray-700">{t('loading') || 'Loading'}...</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="overflow-auto border rounded-lg max-h-96">
                    <Table>
                      <TableHeader className="sticky top-0 bg-gray-50">
                        <TableRow>
                          {(['WorkerFullName', 'ClientName', 'FboWorkOrderNumber', 'StartDate', 'EndDate', 'Stage', 'Status'] as const).map((column) => (
                            <TableHead key={column} className="bg-gray-50 z-10 text-center font-medium">
                              <div className="flex items-center justify-center gap-1">
                                <div 
                                  className="flex items-center gap-1 cursor-pointer hover:bg-gray-100 p-1 rounded"
                                  onClick={() => handleSort(column)}
                                >
                                  <span className="text-xs">{getColumnName(column)}</span>
                                  {sortConfig.column === column ? (
                                    sortConfig.direction === 'asc' ? (
                                      <ArrowUp size={14} className="text-blue-600" />
                                    ) : (
                                      <ArrowDown size={14} className="text-blue-600" />
                                    )
                                  ) : (
                                    <div className="w-3.5 h-3.5" /> // Placeholder for alignment
                                  )}
                                </div>
                                
                                {/* Filter Icon */}
                                <Popover 
                                  open={openFilterColumn === column} 
                                  onOpenChange={(open) => setOpenFilterColumn(open ? column : null)}
                                >
                                  <PopoverTrigger asChild>
                                    <button
                                      className={`p-0.5 rounded hover:bg-gray-100 ${
                                        hasActiveFilter(column) ? 'text-blue-600' : 'text-gray-400'
                                      }`}
                                      onClick={() => toggleFilterPopover(column)}
                                    >
                                      <CustomFilterIcon size={12} />
                                    </button>
                                  </PopoverTrigger>
                                  <PopoverContent className="w-64 p-3" highZIndex={true} forcePointerEvents>
                                    <div className="space-y-3">
                                      {/* Render different content based on column type */}
                                      {(() => {
                                        // Check if it's a date field using the proper utility function
                                        const fieldConfig = configuration?.fieldsInUse?.find(f => f.columnCode === column);
                                        const isDate = fieldConfig ? isDateField(fieldConfig) : false;
                                        
                                        if (isDate) {
                                          // Date filter with From/To fields
                                          const filterValue = columnFilters[column];
                                          const dateValue = (typeof filterValue === 'object' && filterValue && 'start' in filterValue) 
                                            ? filterValue 
                                            : { start: null, end: null };
                                          return (
                                            <>
                                              <div className="space-y-2">
                                                <div>
                                                  <ModernDatePicker
                                                    value={dateValue.start || null}
                                                    onChange={(val) => handleColumnFilterChange(column, { ...dateValue, start: val })}
                                                    placeholder="Start date"
                                                    highZIndex={true}
                                                    forcePointerEvents
                                                  />
                                                </div>
                                                <div>
                                                  <ModernDatePicker
                                                    value={dateValue.end || null}
                                                    onChange={(val) => handleColumnFilterChange(column, { ...dateValue, end: val })}
                                                    placeholder="End date (optional)"
                                                    highZIndex={true}
                                                    forcePointerEvents
                                                  />
                                                </div>
                                              </div>
                                              {/* Same button pairing as the grid's column filter popovers:
                                                  Apply (outline) + Clear All (red). */}
                                              <div className="flex gap-2 pt-1">
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-8 text-xs flex-1"
                                                  onClick={() => setOpenFilterColumn(null)}
                                                >
                                                  {t('apply') || 'Apply'}
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  className="h-8 text-xs flex-1 bg-red-500 hover:bg-red-600 text-white"
                                                  onClick={() => {
                                                    handleColumnFilterChange(column, { start: null, end: null });
                                                    setOpenFilterColumn(null);
                                                  }}
                                                >
                                                  {t('clearAllFilters') || 'Clear All'}
                                                </Button>
                                              </div>
                                            </>
                                          );
                                        } else {
                                          // Regular text filter
                                          const filterValue = columnFilters[column];
                                          const textValue = typeof filterValue === 'string' ? filterValue : '';
                                          return (
                                            <>
                                              <Input
                                                placeholder={`Enter ${getColumnName(column).toLowerCase()}...`}
                                                value={textValue}
                                                onChange={(e) => handleColumnFilterChange(column, e.target.value)}
                                                className="h-9 text-sm rounded-lg"
                                                autoFocus
                                              />
                                              {/* Same button pairing as the grid's column filter popovers:
                                                  Apply (outline) + Clear All (red). */}
                                              <div className="flex gap-2 pt-1">
                                                <Button
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-8 text-xs flex-1"
                                                  onClick={() => setOpenFilterColumn(null)}
                                                >
                                                  {t('apply') || 'Apply'}
                                                </Button>
                                                <Button
                                                  size="sm"
                                                  className="h-8 text-xs flex-1 bg-red-500 hover:bg-red-600 text-white"
                                                  onClick={() => {
                                                    handleColumnFilterChange(column, '');
                                                    setOpenFilterColumn(null);
                                                  }}
                                                >
                                                  {t('clearAllFilters') || 'Clear All'}
                                                </Button>
                                              </div>
                                            </>
                                          );
                                        }
                                      })()}
                                    </div>
                                  </PopoverContent>
                                </Popover>
                              </div>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {memoizedResults.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={7} className="text-center py-8 text-gray-500">
                              No matching records found
                            </TableCell>
                          </TableRow>
                        ) : (
                          paginatedResults.map((item, index) => (
                          <TableRow
                            key={item.id || index}
                            onClick={() => handleRowClick(item)}
                            className="cursor-pointer hover:bg-gray-50 transition-colors focus-within:bg-gray-50 h-8"
                            tabIndex={0}
                            role="button"
                            aria-label={`View details for ${getCellValue(item, 'WorkerFullName')} at ${getCellValue(item, 'ClientName')}`}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                e.preventDefault();
                                handleRowClick(item);
                              }
                            }}
                          >
                            <TableCell className="text-center text-xs py-1 px-2">{getCellValue(item, 'WorkerFullName') || ''}</TableCell>
                            <TableCell className="text-center text-xs py-1 px-2">{getCellValue(item, 'ClientName') || ''}</TableCell>
                            <TableCell className="text-center text-xs py-1 px-2">{getCellValue(item, 'FboWorkOrderNumber') || ''}</TableCell>
                            <TableCell className="text-center text-xs py-1 px-2">{formatDateOnly(getCellValue(item, 'StartDate'), language)}</TableCell>
                            <TableCell className="text-center text-xs py-1 px-2">{formatDateOnly(getCellValue(item, 'EndDate'), language)}</TableCell>
                            <TableCell className="text-center text-xs py-1 px-2">
                              <span className={`inline-flex px-2.5 py-1 text-xs font-medium rounded-full ${
                                (item as any).isRequestRow ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                              }`}>
                                {getCellValue(item, 'Stage') || ''}
                              </span>
                            </TableCell>
                            <TableCell className="text-center text-xs py-1 px-2">
                              {getCellValue(item, 'Status') ? (
                                <span className="inline-flex px-2.5 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700">
                                  {getCellValue(item, 'Status')}
                                </span>
                              ) : ''}
                            </TableCell>
                          </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {totalPages > 1 && (
                    <div className="flex items-center justify-between pt-4">
                      <div className="text-sm text-gray-600">
                        {t('showingRecords', {
                          start: (resultsPage - 1) * ITEMS_PER_PAGE + 1,
                          end: Math.min(resultsPage * ITEMS_PER_PAGE, memoizedResults.length),
                          total: memoizedResults.length
                        }) || `Showing ${(resultsPage - 1) * ITEMS_PER_PAGE + 1}-${Math.min(resultsPage * ITEMS_PER_PAGE, memoizedResults.length)} of ${memoizedResults.length}`}
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setResultsPage(prev => Math.max(1, prev - 1))}
                          disabled={resultsPage === 1}
                        >
                          {t('previous') || 'Previous'}
                        </Button>
                        <span className="text-sm text-gray-600 px-2">
                          {resultsPage} / {totalPages}
                        </span>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setResultsPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={resultsPage === totalPages}
                        >
                          {t('next') || 'Next'}
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {/* ESC key hint */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-in slide-in-from-bottom-4 fade-in duration-500 delay-300">
        <div className="bg-white/80 backdrop-blur-sm px-3 py-1 rounded-full border border-gray-200/50">
          <span className="text-xs text-gray-500">Press ESC to close</span>
        </div>
      </div>
    </div>
  );
}

